import React, { useState } from 'react';
import { TagIcon, ShareIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TextCodeCombinationModal from '../TextCoder/TextCodeCombinationModal';
import TextCodeModal from '../TextCoder/TextCodeModal';
import { updateParentRelationship } from '../../Requests/TextCoderRequests';
import DraggableCode from './DraggableCode';
import BackgroundDropZone from './BackgroundDropZone';
import ParentDropZone from './ParentDropZone';

const Codebook = ({ textCodes, setUploadAudioModal, handleUpdateCode, handleAddNewCode, handleDeleteCode, t }) => {
    const [isReGroupMode, setIsReGroupMode] = useState(false);

    // Get all codes in display order: parents first, then their children indented
    const getOrderedCodes = () => {
        const orderedCodes = [];
        
        // Check if textCodes is defined and is an array
        if (!textCodes || !Array.isArray(textCodes)) {
            return orderedCodes;
        }
        
        const parentCodes = textCodes.filter(code => !code.parent);
        
        parentCodes.forEach(parent => {
            orderedCodes.push(parent);
            const children = textCodes.filter(code => code.parent === parent.uuid);
            children.forEach(child => {
                orderedCodes.push(child);
            });
        });
        
        return orderedCodes;
    };

    const handleDrop = async (draggedCode, targetParentId) => {
        // Store original state for rollback
        const originalParent = draggedCode.parent;
        
        // 1. OPTIMISTIC UPDATE - Update UI immediately
        const updatedCode = { ...draggedCode, parent: targetParentId };
        handleUpdateCode(updatedCode);
        
        try {
            // 2. API CALL - Send request to backend
            await updateParentRelationship({
                code_uuid: draggedCode.uuid,
                parent_uuid: targetParentId
            });
            
            // Success - optimistic update was correct, no action needed
        } catch (error) {
            console.error('Failed to update parent relationship:', error);
            
            // 3. ROLLBACK - Revert UI to original state on failure
            const rolledBackCode = { ...draggedCode, parent: originalParent };
            handleUpdateCode(rolledBackCode);
            
            // Optional: Show error message to user
            // You could add a toast notification here if needed
        }
    };

    const toggleReGroupMode = () => {
        setIsReGroupMode(!isReGroupMode);
    };

    const orderedCodes = getOrderedCodes();

    return (
        <div data-testid="codebook-container">
            {/* Top button bar - responsive layout with proper spacing */}
            <div className='w-full flex flex-col sm:flex-row sm:justify-end items-stretch sm:items-center gap-3 mb-6 mt-2'>
                <div className='flex flex-col sm:flex-row gap-2 w-full sm:w-auto'>
                    <button className='border border-black bg-white text-black text-xs sm:text-sm rounded-md px-3 py-1.5 sm:px-4 sm:py-2 hover:bg-gray-50 transition-colors'
                        data-testid="codebook-join-codes-btn"
                        onClick={() => setUploadAudioModal(
                            <TextCodeCombinationModal
                                handleClose={() => setUploadAudioModal(null)}
                                codes={textCodes}
                                handleCombineCodes={(codesToDelete, newCode) => {
                                    setUploadAudioModal(prev => {
                                        const updatedCodes = prev.filter(code => !codesToDelete.includes(code.uuid));
                                        return [...updatedCodes, newCode];
                                    });
                                }}
                            />
                        )}>
                        <div className='w-full flex justify-center items-center gap-1.5 sm:gap-2 whitespace-nowrap'>
                            <ShareIcon className='w-3.5 h-3.5 sm:w-4 sm:h-4' />
                            <span className='hidden sm:inline'>{t("DetailProjects.JoinCode")}</span>
                            <span className='sm:hidden'>Combinar</span>
                        </div>
                    </button>
                    <button className='border border-black bg-white text-black text-xs sm:text-sm rounded-md px-3 py-1.5 sm:px-4 sm:py-2 hover:bg-gray-50 transition-colors'
                        data-testid="codebook-add-code-btn"
                        onClick={() => setUploadAudioModal(
                            <TextCodeModal
                                handleClose={() => setUploadAudioModal(null)}
                                handleUpdateCode={handleUpdateCode}
                                handleAddNewCode={handleAddNewCode}
                                handleDeleteCode={handleDeleteCode}
                                textCodes={textCodes}
                            />
                        )}>
                        <div className='w-full flex justify-center items-center gap-1.5 sm:gap-2 whitespace-nowrap'>
                            <TagIcon className='w-3.5 h-3.5 sm:w-4 sm:h-4' />
                            <span className='hidden sm:inline'>{t("DetailProjects.Add code")}</span>
                            <span className='sm:hidden'>Adicionar</span>
                        </div>
                    </button>
                    <button 
                        onClick={toggleReGroupMode}
                        className={`flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md border transition-colors ${
                            isReGroupMode 
                                ? 'bg-black text-white border-black hover:bg-gray-800' 
                                : 'bg-white text-black border-black hover:bg-gray-50'
                        }`}
                        data-testid="codebook-regroup-btn"
                    >
                        <ArrowsRightLeftIcon className='w-3.5 h-3.5 sm:w-4 sm:h-4' />
                        <span className='hidden sm:inline'>
                            {isReGroupMode ? t('DetailProjects.Exit Re-organize') : t('DetailProjects.Re-organize')}
                        </span>
                        <span className='sm:hidden'>
                            {isReGroupMode ? 'Sair' : 'Reorganizar'}
                        </span>
                    </button>
                </div>
            </div>

            {textCodes?.length > 0 ? (
                <DndProvider backend={HTML5Backend}>
                    {/* Background drop zone for ungrouping + codes area */}
                    <BackgroundDropZone onDrop={handleDrop} t={t}>
                        <div className='flex flex-col gap-4 p-4' data-testid="codebook-codes-list">
                            {orderedCodes.map((code, index) => {
                                const isParent = !code.parent;
                                
                                return (
                                    <div key={code.uuid}>
                                        {isParent && isReGroupMode ? (
                                            // Parent codes in re-group mode: wrap with drop zone
                                            <ParentDropZone code={code} onDrop={handleDrop}>
                                                <DraggableCode
                                                    code={code}
                                                    textCodes={textCodes}
                                                    setUploadAudioModal={setUploadAudioModal}
                                                    handleUpdateCode={handleUpdateCode}
                                                    handleAddNewCode={handleAddNewCode}
                                                    handleDeleteCode={handleDeleteCode}
                                                    t={t}
                                                    isReGroupMode={isReGroupMode}
                                                />
                                            </ParentDropZone>
                                        ) : (
                                            // All other cases: just draggable
                                            <DraggableCode
                                                code={code}
                                                textCodes={textCodes}
                                                setUploadAudioModal={setUploadAudioModal}
                                                handleUpdateCode={handleUpdateCode}
                                                handleAddNewCode={handleAddNewCode}
                                                handleDeleteCode={handleDeleteCode}
                                                t={t}
                                                isReGroupMode={isReGroupMode}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </BackgroundDropZone>
                </DndProvider>
            ) : (
                <div className='w-full py-12 mt-4 mb-2 rounded-md bg-white p-4 flex flex-col items-center gap-8 flex-grow' data-testid="codebook-empty-state">
                    <div className='text-xl font-bold'>{t('DetailProjects.This project has no codes yet')}</div>
                    <div className='text-base text-gray-500 '>{t('DetailProjects.Add a new code to get started')}</div>
                    <button className='border border-black bg-black text-white text-base sm:text-lg rounded-md px-4 py-2.5 sm:px-6 sm:py-3 hover:bg-gray-800 transition-colors'
                        type='button'
                        data-testid="codebook-empty-add-code-btn"
                        onClick={() => setUploadAudioModal(<TextCodeModal handleClose={() => setUploadAudioModal(null)} handleUpdateCode={handleUpdateCode} handleAddNewCode={handleAddNewCode} textCodes={textCodes} />)}
                    >
                        <div className='w-full flex justify-center items-center gap-2 whitespace-nowrap'>
                            <TagIcon className='w-5 h-5 sm:w-6 sm:h-6' />
                            <span>{t("DetailProjects.Add code")}</span>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Codebook;